import React, { useCallback } from 'react';

export interface PlayerButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Tooltip text shown on hover (also sets aria-label if not provided). */
  tooltip?: string;
  /** Forwarded ref for menu trigger buttons. */
  buttonRef?: React.Ref<HTMLButtonElement>;
}

/**
 * Base button component for all player controls.
 *
 * After a click, blurs the button so keyboard shortcuts (Space for
 * play/pause, arrow keys for seek) continue to work from the player
 * container rather than re-triggering the button. Uses onMouseUp
 * instead of onMouseDown+preventDefault so the browser's :active CSS
 * state is not suppressed.
 */
export const PlayerButton = React.memo(function PlayerButton({
  tooltip,
  buttonRef,
  className,
  children,
  onClick,
  ...rest
}: PlayerButtonProps) {
  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    // Blur so the button doesn't keep focus (Space/Enter would re-trigger it)
    e.currentTarget.blur();
    onClick?.(e);
  }, [onClick]);

  return (
    <button
      ref={buttonRef}
      className={`kollab-player-btn ${className ?? ''}`}
      aria-label={rest['aria-label'] ?? tooltip}
      data-tooltip={tooltip}
      onClick={handleClick}
      type="button"
      {...rest}
    >
      {children ?? <span className="kollab-player-btn-icon" aria-hidden="true" />}
    </button>
  );
});
